import { useQuery } from '@apollo/client/react'
import { dedupe } from '@/helpers/dedupe'
import { Table as BaseTable, type TableProps } from '../table'
import { usePageInfo } from '@/hooks/usePageInfo'
import { Slimline as Error } from '@/components/error'
import { LoadingSkeleton } from '@/components/base/loading-skeleton'
import { graphql } from '@/gql'
import type { ItemTypeFilters, ItemTypeOrder } from '@/gql/graphql'
import { Create } from './create'
import { useState } from 'react'

const ItemTypesQuery = graphql(`
  query ItemTypes($filters: ItemTypeFilters, $order: ItemTypeOrder, $after: String) {
    itemTypes(filters: $filters, order: $order, after: $after) {
      nodes {
        id
        name
      }
      count
      pageInfo { hasNextPage hasPreviousPage startCursor endCursor }
    }
  }
`)

interface Props extends TableProps {
  filters?: ItemTypeFilters
}

export const Table = ({ filters = {}, ...props }: Props) => {
  const { data, loading, error, refetch, fetchMore } = useQuery(ItemTypesQuery, { variables: { filters } })
  const [showCreate, setShowCreate] = useState(false)

  const { pageInfo } = usePageInfo(data?.itemTypes?.pageInfo)

  if (loading) return <LoadingSkeleton table />

  return (
    <>
      { error && <Error error={ error } /> }
      { data && <BaseTable<ItemTypeFilters, ItemTypeOrder>
        { ...props }
        initialFilters={ filters }
        moreToFetch={ pageInfo.current?.hasNextPage }
        refetch={ refetch }
        totalRows={ data.itemTypes.count }
        actionButton={{
          name: 'New',
          color: 'purple',
          callback: () => setShowCreate(true)
        }}
        fetchMore={ () => fetchMore({
          variables: { after: pageInfo.current?.endCursor },
          updateQuery(previousData, { fetchMoreResult }) {
            if (Object.keys(previousData).length === 0) return fetchMoreResult
            fetchMoreResult.itemTypes.nodes = dedupe([...previousData.itemTypes.nodes, ...fetchMoreResult.itemTypes.nodes])
            return { ...fetchMoreResult }
          }
        }) }
        headings={ [
          { name: 'Name' },
        ] }
        rows={ data?.itemTypes?.nodes.map(itemType => ({
          id: itemType.id,
          columns: [
            itemType.name,
          ],
          link: `/item_types/${itemType.id}`,
        })) }
      /> }
      <Create open={ showCreate } setOpen={ setShowCreate } />
    </>
  )
}
